<template>
  <div class="section-inverted-carousel is--full-width">
    <Grid class="grid">
      <SectionHeader
        v-if="props._index !== 0 && title"
        :label="title"
        :button-url="buttonUrl"
        variant="inverted"
        :description="introduction"
      />
      <Carousel v-if="teasers" class="carousel" variant="dark">
        <li v-for="(teaser, index) in articles" :key="`slide_${teaser.id}_${index}`" class="slide">
          <TeaserBlock
            v-bind="teaser"
            aspect-ratio="16x9"
            tag="a"
            :show-date="false"
            variant="dark"
            :padded="true"
            :show-label="!!showLabels"
            aria-label-prefix="ariaLabelPrefix"
            :icon="icon"
            :analytics="{
              linkAnalyticsTag: props.analytics ? props.analytics : `carousel-inverted`,
              linkPosition: index,
            }"
          />
        </li>
        <li v-if="banner" class="slide grid-item banner-item">
          <TeaserBanner
            v-bind="{ ...(banner as ITeaser), priority: 'low', loading: 'lazy' }"
            :is-presenter="false"
            :analytics="{
              linkAnalyticsTag: `${props.analytics || 'spotlight'}-banner`,
            }"
          />
        </li>
      </Carousel>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import type { ICmsBanners, ICmsSectionCarousel } from '~~/types/_cms-types';
import type { ITeaser } from '~~/types/local_types';
import useImagePriority from '~/composables/useImagePriority';

interface IProps extends ICmsSectionCarousel {
  _index: number;
  teasers: ITeaser[];
  banner?: ICmsBanners;
  buttonUrl?: string;
  title?: string;
  introduction?: string;
  icon?: string;
  analytics?: string;
  showLabels?: boolean;
  ariaLabelPrefix?: string;
}

const props = defineProps<IProps>();

const articles = computed(() => {
  return props.teasers?.map((article, index) => {
    return {
      ...(article as ITeaser),
      ...useImagePriority(props._index === 0 ? 'high' : 'auto', 3, index),
    };
  });
});
</script>

<style lang="postcss" scoped>
.carousel {
  margin: -4px 0; /*to show focus outline*/
}

.section-inverted-carousel {
  position: relative;

  background: linear-gradient(
    0deg,
    hsl(var(--color-primary-850)) 0%,
    hsl(var(--color-primary-950)) 100%
  );

  &:before {
    content: '';
    position: absolute;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    background: url('/assets/images/pattern.png') center;
    background-size: calc(var(--unit-base) / 16 * 120);
    opacity: 0.4;

    mask-image:
      linear-gradient(
        264deg,
        #000 6.2%,
        hsla(var(--color-neutral-black), 0.6) 51.21%,
        transparent 75.14%
      ),
      linear-gradient(185deg, hsl(var(--color-neutral-black)) 3.69%, transparent 96.31%);

    mask-composite: intersect;
  }
}

.grid {
  --margin: var(--unit-24);
  margin: var(--margin) 0;

  @media (--md-viewport) {
    --margin: var(--unit-48);
  }

  --grid-gap-default-small: var(--unit-16);
  --grid-gap-default-medium: var(--unit-20);
  --grid-gap-default-large: var(--unit-20);
  --grid-gap-default-extra-large: var(--unit-20);
}

.slide {
  position: relative;
  margin: 4px 0; /*to show focus outline*/
  width: 80%;

  @media (--md-viewport) {
    width: calc(100% / 3.5 - var(--carousel-track-gap));
  }

  > * {
    width: 100%;
    height: 100%;
  }
}
</style>
